import { ComponentProps, ReactNode } from 'react';
import Radio from 'antd/es/radio';

import { OpInfoTooltip } from '../OpInfoTooltip/OpInfoTooltip';

interface OpRadioProps extends ComponentProps<typeof Radio> {
  tooltip?: ReactNode;
  gtm?: string;
}

/**
 * OpRadio is a wrapper around Ant Design's Radio component.
 * It provides a radio button for selecting a single option from a group, with optional tooltip support.
 * 
 * Use this component for forms where users need to select exactly one option from multiple choices.
 * Supports OpRadio.Group for managing multiple radio buttons together, and OpRadio.Button for button-styled radios.
 * 
 * @example
 * <OpRadio.Group value={selected} onChange={handleChange}>
 *   <OpRadio value="option1">Option 1</OpRadio>
 *   <OpRadio value="option2" tooltip="More info about option 2">Option 2</OpRadio>
 * </OpRadio.Group>
 * 
 */
export const OpRadio = ({
  children,
  className = '',
  tooltip,
  gtm,
  ...radioProps
}: OpRadioProps) => {
  return (
    <Radio
      className={`op-radio ${className}`.trim()}
      data-gtm={gtm}
      {...radioProps}
    >
      {children}
      {tooltip && <OpInfoTooltip title={tooltip} />}
    </Radio>
  );
};

/** Need to attach static properties on antd Radio to allow dot
 * notation subcomponents to work on this component as well */
OpRadio.Group = Radio.Group;
OpRadio.Button = Radio.Button;
